import { Resource } from './resource';
import { Filter } from './filters';
import { Sort, SortDirection, SortColumn } from './sort';
import { Sensor, Organization, Note, NoteAction } from './api';
import { Choice } from './choice';
import { OrganizationIdOrAll } from './all-organizations';

export type InConfigurationState = {
  selectedSensorSerialNumbers: Array<Sensor['serial_number']>;

  organizations: Array<{ id: string; name: string }>;

  filters: Array<Filter>;
  searchText: string;
  sort: Sort;

  page: number;

  resource: Resource<{
    total: number;
    sensors: Array<Sensor>;
    fetchingMoreSensors: Resource<null>; // There is no data in this resource, it's only used as a status

    // A list of all possible sensor states fetched from sensei api
    sensorStateChoices: Array<Choice>;
    // A mapping of state names to the human readable version of each
    sensorStateHumanReadableNameMapping: { [state: string]: string };

    csvExport:
      | { status: 'IDLE' }
      | { status: 'LOADING_TOTAL' }
      | { status: 'LOADING_DATA'; totalRows: number; loadedRows: number }
      | { status: 'ERROR'; error: Error };
  }>;
};

export enum InConfigurationActionTypes {
  IN_CONFIGURATION_CHANGE_PAGE = 'IN_CONFIGURATION_CHANGE_PAGE',

  IN_CONFIGURATION_LOAD_START = 'IN_CONFIGURATION_LOAD_START',
  IN_CONFIGURATION_LOAD_ERROR = 'IN_CONFIGURATION_LOAD_ERROR',
  IN_CONFIGURATION_LOAD_COMPLETE = 'IN_CONFIGURATION_LOAD_COMPLETE',

  IN_CONFIGURATION_SET_FILTERS = 'IN_CONFIGURATION_SET_FILTERS',
  IN_CONFIGURATION_SET_SEARCH_TEXT = 'IN_CONFIGURATION_SET_SEARCH_TEXT',
  IN_CONFIGURATION_SET_SORT = 'IN_CONFIGURATION_SET_SORT',

  IN_CONFIGURATION_CHANGE_CHECKS = 'IN_CONFIGURATION_CHANGE_CHECKS',

  IN_CONFIGURATION_SET_PAGE = 'IN_CONFIGURATION_LIST_SET_PAGE',
  IN_CONFIGURATION_NEXT_PAGE_LOAD_LOADING = 'IN_CONFIGURATION_LIST_NEXT_PAGE_LOAD_LOADING',
  IN_CONFIGURATION_NEXT_PAGE_LOAD_ERROR = 'IN_CONFIGURATION_LIST_NEXT_PAGE_LOAD_ERROR',
  IN_CONFIGURATION_NEXT_PAGE_LOAD_COMPLETE = 'IN_CONFIGURATION_LIST_NEXT_PAGE_LOAD_COMPLETE',

  IN_CONFIGURATION_CSV_EXPORT = 'IN_CONFIGURATION_CSV_EXPORT',
  IN_CONFIGURATION_CSV_EXPORT_TOTAL_LOADED = 'IN_CONFIGURATION_CSV_EXPORT_TOTAL_LOADED',
  IN_CONFIGURATION_CSV_EXPORT_DATA_PAGE_LOADED = 'IN_CONFIGURATION_CSV_EXPORT_DATA_PAGE_LOADED',
  IN_CONFIGURATION_CSV_EXPORT_COMPLETE = 'IN_CONFIGURATION_CSV_EXPORT_COMPLETE',
  IN_CONFIGURATION_CSV_EXPORT_CANCEL = 'IN_CONFIGURATION_CSV_EXPORT_CANCEL',
  IN_CONFIGURATION_CSV_EXPORT_ERROR = 'IN_CONFIGURATION_CSV_EXPORT_ERROR',
  IN_CONFIGURATION_CLICK_NOTE_ACTION = 'IN_CONFIGURATION_CLICK_NOTE_ACTION',
}

export type InConfigurationAction =
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CHANGE_PAGE;
      searchText: string;
      filters: Array<Filter>;
      sort: Sort;
      organizationId: Organization['id'] | null;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_LOAD_START;
      page: 'list' | 'detail';
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_LOAD_ERROR;
      page: 'list' | 'detail';
      error: any;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_LOAD_COMPLETE;
      sensors: Array<Sensor>;
      total: number;
      organizations: Array<{ id: string; name: string }>;
      searchText: string;
      filters: Array<Filter>;
      sort: Sort;
      sensorStateChoices: Array<Choice>;
      sensorStateHumanReadableNameMapping: { [state: string]: string };
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_SET_SEARCH_TEXT;
      text: string;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_SET_FILTERS;
      filters: Array<Filter>;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_SET_SORT;
      direction: SortDirection;
      column: SortColumn;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CHANGE_CHECKS;
      serialNumbers: Array<Sensor['serial_number']>;
      checked: boolean;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_SET_PAGE;
      value: number;
    }
  | { type: InConfigurationActionTypes.IN_CONFIGURATION_NEXT_PAGE_LOAD_LOADING }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_NEXT_PAGE_LOAD_ERROR;
      error: any;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_NEXT_PAGE_LOAD_COMPLETE;
      sensors: Array<Sensor>;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT;
      organizationId: Organization['id'] | null;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_TOTAL_LOADED;
      initiallyLoadedRows: number;
      totalRows: number;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_DATA_PAGE_LOADED;
      pageLength: number;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_COMPLETE;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_CANCEL;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_ERROR;
      error: Error;
    }
  | {
      type: InConfigurationActionTypes.IN_CONFIGURATION_CLICK_NOTE_ACTION;
      serialNumber: Sensor['serial_number'];
      noteId: Note['id'];
      noteAction: NoteAction;
    };
